import { Link, useRouter } from "@shared/client/navigation";
import { useEffect, useState } from "react";
import { Check, CircleAlert } from "lucide-react";
import { cn } from "@shared/lib/utils";
import { setTabStatus } from "@shared/client/tab-status";
import { notifyPassEnded, type PassOutcomeNotice } from "@/components/layout/pass-notification";
import { useT } from "@shared/client/locale";

/**
 * Le passage en cours, dans la topbar.
 *
 * L'agent travaille côté serveur : quitter l'écran d'accueil, changer d'onglet
 * ou fermer le navigateur n'annule rien. Mais seule la carte de veille le
 * savait — ailleurs dans le produit, plus aucun signe, et l'utilisateur ne
 * pouvait pas distinguer « ça cherche » de « ça a été annulé ». Cette pastille
 * suit le passage depuis n'importe quel écran et dit ce qu'il devient.
 *
 * Elle se recale à chaque retour d'onglet (`visibilitychange`) plutôt que
 * d'attendre le prochain battement : c'est le moment précis où l'on revient
 * poser la question.
 */

export type RunningPassView = {
  runId: string;
  startedAt: string;
  /** Nom de l'offre analysée, affiché quand le compte en surveille plusieurs. */
  offerName: string | null;
  source: "manual" | "radar";
};

type Phase = "running" | "completed" | "failed";

const POLL_MS = 12_000;
const TICK_MS = 30_000;
/** Au-delà, un passage sans nouvelles a probablement été interrompu côté serveur. */
const STALLED_MS = 15 * 60_000;
/** Durée d'affichage du dénouement avant que la pastille s'efface. */
const OUTCOME_MS = 90_000;

function elapsedMinutes(startedAt: string, now: number) {
  return Math.max(0, Math.floor((now - Date.parse(startedAt)) / 60_000));
}

/** L'astérisque du logo, qui tourne tant que l'agent cherche (le radar de la carte de veille, en miniature). */
function SpinningAsterisk() {
  return (
    <svg aria-hidden="true" className="size-3.5 flex-none" viewBox="-20 -20 40 40">
      <g className="radar-spin" style={{ animationDuration: "4s" }}>
        {[0, 45, 90, 135].map((angle) => (
          <rect className="fill-primary" height={34} key={angle} rx={1} transform={`rotate(${angle})`} width={7} x={-3.5} y={-17} />
        ))}
      </g>
    </svg>
  );
}

export function RunIndicator({ pass }: { pass: RunningPassView | null }) {
  const router = useRouter();
  const copy = useT().shell.runIndicator;
  // L'horloge est figée au montage puis avancée par un intervalle : lire
  // Date.now() pendant le rendu rendrait le composant non idempotent.
  const [now, setNow] = useState(() => Date.now());
  const [tracked, setTracked] = useState<RunningPassView | null>(pass);
  const [phase, setPhase] = useState<Phase>("running");

  // Le serveur annonce un passage : on le prend en charge (ajustement d'état
  // pendant le rendu, le motif prévu pour « une prop a changé »). Il cesse
  // ensuite de l'annoncer dès qu'il est terminé — on garde alors la main pour
  // montrer le dénouement, que ce rafraîchissement effacerait aussitôt.
  if (pass && pass.runId !== tracked?.runId) {
    setTracked(pass);
    setPhase("running");
  }

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, []);

  const runId = tracked?.runId ?? null;
  const offerName = tracked?.offerName;

  // L'onglet du navigateur suit le passage : « Analyse en cours… » tant qu'il tourne.
  const running = Boolean(tracked) && phase === "running";
  useEffect(() => {
    setTabStatus({ running });
    return () => setTabStatus({ running: false });
  }, [running]);

  useEffect(() => {
    if (!runId || phase !== "running") return;
    let cancelled = false;

    async function check() {
      try {
        const response = await fetch(`/api/runs/${runId}`);
        if (!response.ok) return;
        const data = (await response.json()) as { status?: string; outcome?: PassOutcomeNotice | null };
        if (cancelled) return;
        setNow(Date.now());
        if (data.status === "completed") {
          setPhase("completed");
          notifyPassEnded({ ok: true, outcome: data.outcome ?? null, offerName: offerName ?? null }, () => router.push("/app/leads"));
          // Le bilan, le pipeline et les compteurs viennent du serveur : lui
          // seul sait ce que ce passage a déposé.
          router.refresh();
        } else if (data.status === "failed" || data.status === "cancelled") {
          setPhase("failed");
          notifyPassEnded({ ok: false, outcome: null, offerName: offerName ?? null }, () => router.push("/app#watch"));
        }
      } catch {
        // Réseau capricieux : on retentera au battement suivant.
      }
    }

    const timer = setInterval(check, POLL_MS);
    function onVisibility() {
      if (document.visibilityState === "visible") void check();
    }
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [runId, phase, router, offerName]);

  // Le dénouement s'efface tout seul : une pastille « terminé » qui reste une
  // heure ne veut plus rien dire.
  useEffect(() => {
    if (phase === "running") return;
    const timer = setTimeout(() => setTracked(null), OUTCOME_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  if (!tracked) return null;

  const stalled = phase === "running" && now - Date.parse(tracked.startedAt) > STALLED_MS;
  const tone = phase === "completed" ? "done" : phase === "failed" || stalled ? "warn" : "busy";

  const label =
    phase === "completed"
      ? copy.completed
      : phase === "failed"
        ? copy.failed
        : stalled
          ? copy.stalled
          : tracked.source === "radar"
            ? copy.radarSearching
            : copy.agentSearching;

  const detail =
    phase === "completed"
      ? copy.seeOutcome
      : phase === "failed"
        ? copy.seeReason
        : stalled
          ? copy.noSignSince(elapsedMinutes(tracked.startedAt, now))
          : copy.elapsed(elapsedMinutes(tracked.startedAt, now));

  return (
    <Link
      className={cn(
        "flex h-[38px] min-w-0 items-center gap-2 rounded-lg border px-3 text-[12px] font-semibold transition-colors",
        tone === "busy" && "border-border bg-background text-foreground hover:bg-muted",
        tone === "done" && "border-success/30 text-success hover:bg-success/5",
        tone === "warn" && "border-destructive/30 text-destructive hover:bg-destructive/5",
      )}
      href="/app#watch"
      title={
        phase === "running" && !stalled
          ? copy.background
          : undefined
      }
    >
      {tone === "busy" && <SpinningAsterisk />}
      {tone === "done" && <Check aria-hidden="true" className="size-3.5" />}
      {tone === "warn" && <CircleAlert aria-hidden="true" className="size-3.5" />}
      <span className="truncate">
        {label}
        {tracked.offerName && phase === "running" && !stalled && (
          <span className="font-normal text-muted-foreground max-lg:hidden"> · {tracked.offerName}</span>
        )}
      </span>
      <span className="flex-none font-normal text-muted-foreground max-md:hidden">{detail}</span>
    </Link>
  );
}
